/**
 * Second-step helpers of the Appointment module (pure, no React, no network): reminders, tests chosen at booking,
 * export and the printable slip. The server owns every rule; these only prepare what the UI shows or sends.
 */
import { dateOnly, formatDateLabel, statusMeta, toIsoDate } from './appointmentLogic';

const isOpen = (a) => !!a && (a.status === 'BOOKED' || a.status === 'CONFIRMED');

/** Country code put in front of a 10-digit Indian number for WhatsApp links. */
export const DEFAULT_COUNTRY_CODE = '91';

/** International digits for wa.me, or null when the number cannot be a mobile number. */
export const toWhatsAppNumber = (mobile, countryCode = DEFAULT_COUNTRY_CODE) => {
  const raw = String(mobile || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `${countryCode}${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `${countryCode}${digits.slice(1)}`;
  if (raw.startsWith('+') && digits.length >= 11 && digits.length <= 15) return digits;
  if (digits.length >= 11 && digits.length <= 15 && digits.startsWith(countryCode)) return digits;
  return null;
};

export const buildReminderMessage = (a, centreName) => {
  const who = `${a.title ? `${a.title} ` : ''}${a.pName}`.trim();
  const when = `${formatDateLabel(a.appointmentDate)} at ${a.slotStart}`;
  const where = centreName || a.centreName || 'our centre';
  const home = a.visitType && /home/i.test(a.visitType) ? ' Our team will visit you for sample collection.' : '';
  return `Dear ${who}, this is a reminder of your appointment at ${where} on ${when} (Ref ${a.appointmentNo}).${home} Please arrive on time. To change it, call us.`;
};

export const whatsAppLink = (a, centreName) => {
  const number = toWhatsAppNumber(a.mobile);
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(buildReminderMessage(a, centreName))}` : null;
};

/** Open appointment of today or tomorrow that has not been reminded yet (same rule as the server's "reminders due"). */
export const isReminderDue = (a, now = new Date()) => {
  if (!isOpen(a) || a.reminderSentAt) return false;
  const day = dateOnly(a.appointmentDate);
  const today = toIsoDate(now);
  const tomorrow = toIsoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  return day === today || day === tomorrow;
};

/** A test from the Investigation catalogue as the appointment stores it (registration re-prices it, so the rate is only an estimate). */
export const testFromCatalogItem = (inv) => ({
  investigationId: inv.investigationId || 0,
  itemId: inv.itemId,
  itemType: inv.itemType || 'TEST',
  code: inv.code || inv.testCode || '',
  name: inv.name,
  category: inv.category || inv.departmentName || '',
  sample: inv.sample || inv.primarySampleName || '',
  componentsCount: inv.componentsCount || 1,
  baseRate: Number(inv.baseRate) || 0,
  reportType: inv.reportType || 0,
  subCategoryId: inv.subCategoryId || 0,
  sampleTypeId: inv.sampleTypeId || inv.defaultSampleTypeId || 0,
  containerId: inv.containerId || inv.defaultContainerId || 0,
});

/**
 * A catalogue entry (the shape the registration screen's order cart takes) from an Investigation row of the master list, the same
 * mapping the registration search uses. The appointment only remembers which test; everything else comes from here.
 */
export const catalogItemFromInvestigation = (inv) => ({
  itemType: 'TEST',
  investigationId: inv.investigationId,
  itemId: inv.itemId,
  code: inv.testCode,
  name: inv.name,
  category: inv.departmentName || 'Diagnostics',
  sample: inv.primarySampleName || 'Blood / Serum',
  componentsCount: 1,
  baseRate: parseFloat(inv.baseRate) || 0,
  reportType: inv.reportType || 0,
  subCategoryId: inv.subCategoryId,
  sampleTypeId: inv.defaultSampleTypeId,
  containerId: inv.defaultContainerId,
  processingMode: inv.processingMode || 'IN_HOUSE',
  isOutsource: inv.processingMode === 'OUTSOURCE' || Boolean(inv.defaultReferenceLabId) || Boolean(inv.referenceLabName),
  referenceLabName: inv.referenceLabName || null,
  defaultReferenceLabId: inv.defaultReferenceLabId || null,
});

export const estimateTotal = (tests) => (tests || []).reduce((sum, t) => sum + (Number(t.baseRate) || 0), 0);

export const formatMoney = (n) =>
  `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const csvCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  // Stop a spreadsheet from running a cell that starts like a formula, and quote when needed.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const APPOINTMENT_CSV_COLUMNS = [
  ['Appointment No', (a) => a.appointmentNo],
  ['Date', (a) => dateOnly(a.appointmentDate)],
  ['Slot', (a) => `${a.slotStart}-${a.slotEnd}`],
  ['Status', (a) => statusMeta(a.status).label],
  ['Token', (a) => a.tokenNo ?? ''],
  ['Patient', (a) => `${a.title || ''} ${a.pName}`.trim()],
  ['UHID', (a) => a.patientId || ''],
  ['Gender', (a) => a.gender],
  ['Age', (a) => a.ageYear ?? ''],
  ['Mobile', (a) => a.mobile],
  ['Centre', (a) => a.centreName],
  ['Department', (a) => a.departmentName || ''],
  ['Visit type', (a) => a.visitType],
  ['Doctor', (a) => a.doctorName || ''],
  ['Purpose', (a) => a.purpose || ''],
  ['Tests', (a) => (a.tests || []).map((t) => t.name).join('; ')],
  ['Estimated amount', (a) => a.estimatedAmount ?? ''],
  ['Arrived at', (a) => (a.arrivedAt ? String(a.arrivedAt).replace('T', ' ').slice(0, 16) : '')],
  ['Registered LabNo', (a) => a.registeredLabNo || ''],
  ['Cancel reason', (a) => a.cancelReason || ''],
  ['Booked by', (a) => a.createdBy || ''],
];

export const appointmentsToCsv = (items) => {
  const header = APPOINTMENT_CSV_COLUMNS.map(([h]) => csvCell(h)).join(',');
  const rows = (items || []).map((a) => APPOINTMENT_CSV_COLUMNS.map(([, get]) => csvCell(get(a))).join(','));
  return [header, ...rows].join('\r\n');
};

export const reportToCsv = (report) => {
  const lines = [['Section', 'Label', 'Total', 'Registered', 'No-show', 'Cancelled', 'Open'].map(csvCell).join(',')];
  const add = (section, rows) => (rows || []).forEach((r) => lines.push([section, r.label, r.total, r.registered, r.noShow, r.cancelled, r.open].map(csvCell).join(',')));
  add('By department', report.byDepartment);
  add('By day', report.byDay);
  return lines.join('\r\n');
};

export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** A self-contained printable slip. Every value is escaped, so patient text can never inject markup. */
export const buildSlipHtml = (a, { centreName, instructions } = {}) => {
  const e = escapeHtml;
  const rows = [
    ['Patient', `${a.title || ''} ${a.pName}`.trim()],
    ['UHID', a.patientId || 'New patient'],
    ['Mobile', a.mobile],
    ['Department', a.departmentName],
    ['Visit', a.visitType],
    ['Referring doctor', a.doctorName],
    ['Purpose', a.purpose],
    ['Collection address', a.collectionAddress ? `${a.collectionAddress}${a.collectionPincode ? ` - ${a.collectionPincode}` : ''}` : ''],
  ].filter(([, v]) => v);
  const tests = (a.tests || []).map((t) => `<li>${e(t.name)}</li>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Appointment ${e(a.appointmentNo)}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:18px;color:#0f172a;max-width:380px}
  h1{font-size:16px;margin:0 0 2px} .sub{font-size:12px;color:#475569;margin-bottom:10px}
  .box{border:2px solid #0f172a;border-radius:8px;padding:10px;text-align:center;margin:10px 0}
  .box .when{font-size:20px;font-weight:700} .box .no{font-size:12px;margin-top:4px;letter-spacing:.04em}
  .token{font-size:34px;font-weight:800;margin-top:6px}
  table{width:100%;border-collapse:collapse;font-size:12px} td{padding:3px 0;vertical-align:top} td:first-child{color:#475569;width:38%}
  .note{font-size:11px;border-top:1px dashed #94a3b8;margin-top:10px;padding-top:8px;white-space:pre-wrap}
  ul{margin:4px 0 0 16px;padding:0;font-size:12px}
  @media print{body{padding:0}}
</style></head><body>
<h1>${e(centreName || a.centreName || 'Appointment')}</h1>
<div class="sub">Appointment slip</div>
<div class="box"><div class="when">${e(formatDateLabel(a.appointmentDate))}</div><div class="when">${e(a.slotStart)} - ${e(a.slotEnd)}</div>
<div class="no">${e(a.appointmentNo)}</div>${a.tokenNo ? `<div class="token">Token ${e(a.tokenNo)}</div>` : ''}</div>
<table>${rows.map(([k, v]) => `<tr><td>${e(k)}</td><td>${e(v)}</td></tr>`).join('')}</table>
${tests ? `<div style="margin-top:8px;font-size:12px"><strong>Tests</strong><ul>${tests}</ul></div>` : ''}
${instructions ? `<div class="note"><strong>Instructions</strong>\n${e(instructions)}</div>` : ''}
<div class="note">Please carry this slip. Billing is done at the registration desk.</div>
</body></html>`;
};

/** Minutes a patient who has arrived has been waiting for registration; null when they have not arrived. */
export const waitingMinutes = (a, now = new Date()) =>
  a && a.arrivedAt ? Math.max(0, Math.round((now - new Date(a.arrivedAt)) / 60000)) : null;

/**
 * Printing needs a window opened straight from the click (browsers block pop-ups opened later), so the page opens it first,
 * then fetches what the slip needs, then writes the slip into that window.
 */
export const openPrintWindow = () => {
  const w = typeof window !== 'undefined' ? window.open('', '_blank', 'width=460,height=700') : null;
  if (w) {
    w.document.open();
    w.document.write('<p style="font-family:Arial;padding:16px">Preparing the slip…</p>');
    w.document.close();
  }
  return w;
};

export const printSlipInto = (w, a, options) => {
  w.document.open();
  w.document.write(buildSlipHtml(a, options));
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) { /* the user can print from the window */ } }, 250);
};

export const downloadTextFile = (filename, text, mime = 'text/csv;charset=utf-8') => {
  // A byte-order mark makes Excel read the file as UTF-8.
  const blob = new Blob(['﻿', text], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
